import { put, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { withCallback } from 'redux-saga-callback'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import { message } from 'antd'
import {
  list as botsList,
  get as botGet,
  add as botAdd,
  update as botUpdate,
  del as botDelete,
} from 'services/bots'
import {
  list as chatWidgetListService,
  add as chatWidgetAdd,
  update as chatWidgetUpdate,
} from 'services/chatWidgets'
import { list as extensionsList } from 'services/extensions'
import {
  add as builderAdd,
  list as builderList,
  get as builderGet,
  update as builderUpdate,
  reset as builderReset,
} from 'services/builders'
import { getNotificationLogs } from 'services/notificationsLogs'
import numberToWords from 'number-to-words'
import constants from 'constants'
import _ from 'lodash'
import { getAppHost, objectToParams, isAuthErrorCode, checkIfFeatureRestricted } from 'helpers'
import actions from './actions'

export function* BOTS_LIST({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areBotsLoading: true,
      },
    })
    if (!payload.data) payload.data = reduxStore.getState().apps.active
    if (!payload.appSettings) payload.appSettings = reduxStore.getState().apps.appSettings
    const botsPagination = reduxStore.getState().misc.botsListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && botsPagination.current_page !== undefined)
      pageNo = botsPagination.current_page + 1

    const success = yield call(botsList, payload.data, getAppHost(payload.appSettings), pageNo)

    if (success && success.data) {
      let totalBotCount = success.data.length
      let botsListPagination = []
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalBotCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        botsListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          botsList: success.data,
          botsListTotal: totalBotCount,
          botsListPagination,
          areBotsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          botsList: [],
          botsListTotal: 0,
          botsListPagination: [],
          areBotsLoading: false,
        },
      })
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        botsList: [],
        botsListTotal: 0,
        botsListPagination: [],
        areBotsLoading: false,
      },
    })
  }
}

export function* GET_BOT({ payload }) {
  try {
    const { id } = payload
    if (!payload.data) payload.data = reduxStore.getState().apps.active
    if (!payload.appSettings) payload.appSettings = reduxStore.getState().apps.appSettings

    const success = yield call(botGet, id, payload.data, getAppHost(payload.appSettings))
    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          botsActive: success.data,
        },
      })
    } else {
      yield put(push(`/app/${payload.data.id}/third-party-ai-agents`))
    }
  } catch (e) {
    yield put(push(`/app/${reduxStore.getState().apps.active.id}/third-party-ai-agents`))
  }
}

export function* ADD_BOT({ payload }) {
  try {
    const activeApp = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps

    const success = yield call(botAdd, payload, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.BOTS_LIST,
        payload: { currentPage: 1, data: activeApp },
      })

      yield put(push(`/app/${activeApp.id}/third-party-ai-agents`))
      window.triggerNotification('Agent added', 'success')
    } else if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        window.triggerNotification('Plan restriction - Feature not available', 'error')
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* UPDATE_BOT({ id, payload }) {
  try {
    const activeApp = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps
    const filtered = {}
    const unset = []
    _.filter(payload, function fn(n, k) {
      if (k === 'useBasicAuth' || k === 'enabled' || (payload[k] && _.trim(payload[k]) !== '')) {
        filtered[k] = n
      } else {
        unset.push(k)
      }
    })
    filtered.unset = unset
    const success = yield call(botUpdate, id, filtered, activeApp, getAppHost(appSettings))
    if (success && success.data) {
      yield put({
        type: actions.GET_BOT,
        payload: { id },
      })

      yield put({
        type: actions.BOTS_LIST,
        payload: { currentPage: 1, data: activeApp },
      })

      yield put(push(`/app/${activeApp.id}/third-party-ai-agents`))
      window.triggerNotification('Agent saved', 'success')
    } else if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        window.triggerNotification('Plan restriction - Feature not available', 'error')
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    return success
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* DELETE_BOT({ payload }) {
  try {
    const { id, refresh } = payload

    const activeApp = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps
    const { botsListPagination } = reduxStore.getState().misc

    const success = yield call(botDelete, id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const botListPageNo =
        botsListPagination !== undefined && botsListPagination.count > 1
          ? botsListPagination.current_page
          : 1
      yield put({
        type: actions.BOTS_LIST,
        payload: { currentPage: botListPageNo, data: activeApp },
      })

      window.triggerNotification('Agent deleted', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/third-party-ai-agents`))
    } else if (success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      }
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* CHANGE_BOT_STATUS({ payload }) {
  try {
    const { id, status } = payload

    const activeApp = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps

    const allBots = reduxStore.getState().misc.botsList

    const data = { enabled: !status }

    const success = yield call(botUpdate, id, data, activeApp, getAppHost(appSettings))

    let successMessage

    if (success && success.data) {
      _.forEach(allBots, function fn(c, k) {
        if (c.uid === id) {
          if (allBots[k].enabled) {
            successMessage = 'disabled'
          } else {
            successMessage = 'enabled'
          }

          allBots[k].enabled = !allBots[k].enabled
        }
      })

      yield put({
        type: actions.SET_STATE,
        payload: {
          botsList: allBots,
        },
      })
      window.triggerNotification(`Agent ${successMessage}`, 'success')
    } else if (success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      }
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* CHAT_WIDGETS_LIST({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areWidgetsLoading: true,
      },
    })
    const appData = reduxStore.getState().apps.active
    let chatWidgetProcessingCompleting = false
    const allExtensions = yield call(extensionsList, appData, 'installed')
    let chatWidgetListCall = false
    if (allExtensions && allExtensions.data) {
      let chatWidgetsList = []
      let chatWidgetsCombinedList = []
      let chatWidgetAppUrl = false
      let chatWidgetActive = []
      if (!_.isEmpty(allExtensions.data)) {
        const checkIndex = _.findIndex(allExtensions.data, function fn2(a) {
          return a.id === 'widget'
        })
        if (checkIndex >= 0) {
          chatWidgetAppUrl = allExtensions.data[checkIndex].adminURL
        }
        if (chatWidgetAppUrl) {
          chatWidgetAppUrl = _.replace(chatWidgetAppUrl, 'show-setting', 'settings')
          chatWidgetAppUrl = _.replace(chatWidgetAppUrl, 'v1', 'v2')
          chatWidgetListCall = yield call(chatWidgetListService, appData, chatWidgetAppUrl)
          if (chatWidgetListCall && chatWidgetListCall.data) {
            chatWidgetsList = chatWidgetListCall.data
            chatWidgetsCombinedList = chatWidgetListCall.data
            chatWidgetsList = _.filter(chatWidgetsList, function fn(cwl) {
              return cwl.configuration.version !== undefined && cwl.configuration.version === 'v2'
            })
          }
          if (payload !== undefined && payload.id !== undefined) {
            const { id } = payload
            if (id !== undefined && id && id.length) {
              const checkIndexWidget = _.findIndex(chatWidgetsList, function fn2(a) {
                return a.widgetId === id
              })

              if (checkIndexWidget >= 0) {
                chatWidgetActive = chatWidgetsList[checkIndexWidget]
              }
            }
          }
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          chatWidgetsList,
          chatWidgetsCombinedList,
          chatWidgetAppUrl,
          chatWidgetActive,
          areWidgetsLoading: false,
        },
      })
      if (payload.redirectToCustomize !== undefined && payload.redirectToCustomize) {
        yield put(push(`/app/${appData.id}/widgets/${payload.id}/detail`))
      } else if (payload !== undefined && payload.id !== undefined && _.isEmpty(chatWidgetActive)) {
        yield put(push(`/app/${appData.id}/widgets`))
      }
      chatWidgetProcessingCompleting = chatWidgetActive
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          chatWidgetsList: [],
          chatWidgetsCombinedList: [],
          chatWidgetAppUrl: false,
          chatWidgetActive: [],
          areWidgetsLoading: false,
        },
      })
      if (
        allExtensions.error !== undefined &&
        allExtensions.error.code !== undefined &&
        isAuthErrorCode(allExtensions.error.code)
      ) {
        if (allExtensions.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
          yield put({
            type: 'user/LOGOUT',
          })
        } else yield put(push('/login'))
      }
      chatWidgetProcessingCompleting = true
    }
    return chatWidgetProcessingCompleting
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        chatWidgetsList: [],
        chatWidgetsCombinedList: [],
        chatWidgetAppUrl: false,
        chatWidgetActive: [],
        areWidgetsLoading: false,
      },
    })
    return true
  }
}

export function* GET_CHAT_WIDGET({ payload }) {
  try {
    if (!payload.data) payload.data = reduxStore.getState().apps.active

    const { chatWidgetsList } = reduxStore.getState().misc
    const { id } = payload
    const checkIndex = _.findIndex(chatWidgetsList, function fn2(a) {
      return a.widgetId === id
    })
    let chatWidgetActive = []
    if (checkIndex >= 0) {
      chatWidgetActive = chatWidgetsList[checkIndex]
      chatWidgetActive.widgetId = id
    }

    yield put({
      type: actions.SET_STATE,
      payload: {
        chatWidgetActive,
      },
    })
    if (!chatWidgetActive.length) {
      yield put(push(`/app/${payload.data.id}/widgets`))
    }
  } catch (e) {
    yield put(push(`/app/${reduxStore.getState().apps.active.id}/widgets`))
  }
}

export function* ADD_CHAT_WIDGET() {
  try {
    const { chatWidgetAppUrl } = reduxStore.getState().misc
    const currentChatWidgetsList = reduxStore.getState().misc.chatWidgetsList
    const currentCombinedChatWidgetsList = reduxStore.getState().misc.chatWidgetsCombinedList
    const currentChatWidgetsIds = _.map(currentCombinedChatWidgetsList, 'widgetId')
    const activeApp = reduxStore.getState().apps.active
    const defaultChatWidgetName = `My ${numberToWords.toWordsOrdinal(
      currentChatWidgetsList.length + 1,
    )} chat`
    let enableVoiceCallingValue = true
    let enableVideoCallingValue = true
    let enableSoundForCallingValue = true
    let enableCallingNotificationsValue = true
    const enableCallRecordingValue = false

    enableVoiceCallingValue = !(
      checkIfFeatureRestricted(activeApp, 'one_on_one_voice_call') &&
      checkIfFeatureRestricted(activeApp, 'group_voice_call')
    )
    enableVideoCallingValue = !(
      checkIfFeatureRestricted(activeApp, 'one_on_one_video_call') &&
      checkIfFeatureRestricted(activeApp, 'group_video_call')
    )

    enableSoundForCallingValue = !(
      checkIfFeatureRestricted(activeApp, 'one_on_one_voice_call') &&
      checkIfFeatureRestricted(activeApp, 'group_voice_call') &&
      checkIfFeatureRestricted(activeApp, 'one_on_one_video_call') &&
      checkIfFeatureRestricted(activeApp, 'group_video_call')
    )

    enableCallingNotificationsValue = !(
      checkIfFeatureRestricted(activeApp, 'one_on_one_voice_call') &&
      checkIfFeatureRestricted(activeApp, 'group_voice_call') &&
      checkIfFeatureRestricted(activeApp, 'one_on_one_video_call') &&
      checkIfFeatureRestricted(activeApp, 'group_video_call')
    )

    const filtered = {
      ids: currentChatWidgetsIds,
      settings: {
        version: 'v2',
        name: defaultChatWidgetName,
        style: {
          primary_color: '#03A9F4',
          foreground_color: '#000000',
          background_color: '#FFFFFF',
          override_system_background_colors: true,
          custom_js: '',
          custom_css: '',
          docked_layout_icon_open: constants.chatWidgetDefaults.docked_layout_icon_open,
          docked_layout_icon_close: constants.chatWidgetDefaults.docked_layout_icon_close,
          docked_layout_icon_background: constants.chatWidgetDefaults.docked_layout_icon_background,
        },
        sidebar: {
          recent_chat_listing: 'all_chats',
          user_listing: 'all_users',
          group_listing: 'public_and_password_protected_groups',
          chats: true,
          users: true,
          groups: true,
          calls: false,
          user_settings: false,
          sidebar_navigation_sequence: ['chats', 'users', 'groups', 'calls', 'settings'],
          start_a_new_conversation: 'all_chats',
        },
        main: {
          enable_sending_messages: true,
          allow_mention_members: false,
          enable_editing_messages: true,
          enable_deleting_messages: true,
          hide_deleted_messages: false,
          enable_replying_to_messages: false,
          enable_threaded_replies: !checkIfFeatureRestricted(
            activeApp,
            'threaded_conversations_and_replies',
          ),
          enable_share_copy_forward_messages: false,
          highlight_messages_from_moderators: false,
          enable_voice_calling: enableVoiceCallingValue,
          enable_video_calling: enableVideoCallingValue,
          show_call_recording_option: enableCallRecordingValue,
          send_photos_videos: !checkIfFeatureRestricted(activeApp, 'share_files_and_multimedia'),
          send_files: true,
          send_voice_notes: !checkIfFeatureRestricted(activeApp, 'record_send_voice_messages'),
          send_emojis: !checkIfFeatureRestricted(activeApp, 'extension_emojis'),
          show_emojis_in_larger_size: true,
          send_gifs: false,
          share_live_reactions: !checkIfFeatureRestricted(activeApp, 'live_reactions'),
          share_location: false,
          set_groups_in_qna_mode_by_moderators: false,
          create_groups: true,
          allow_add_members: true,
          join_or_leave_groups: true,
          hide_join_leave_notifications: false,
          allow_delete_groups: true,
          view_group_members: true,
          view_shared_media: true,
          send_message_in_private_to_group_member: false,
          show_typing_indicators: !checkIfFeatureRestricted(activeApp, 'typing_indicators'),
          show_delivery_read_indicators: !checkIfFeatureRestricted(activeApp, 'read_receipts'),
          show_user_presence: !checkIfFeatureRestricted(activeApp, 'user_presence'),
          show_call_notifications: enableCallingNotificationsValue,
          block_user: !checkIfFeatureRestricted(activeApp, 'user_to_user_blocking'),
          allow_moderator_to_delete_member_messages: false,
          allow_kick_ban_members: !(
            checkIfFeatureRestricted(activeApp, 'group_moderators') ||
            checkIfFeatureRestricted(activeApp, 'group_kick_member') ||
            checkIfFeatureRestricted(activeApp, 'group_ban_member')
          ),
          allow_promote_demote_members: !checkIfFeatureRestricted(activeApp, 'group_moderators'),
          allow_creating_polls: false,
          enable_sound_for_calls: enableSoundForCallingValue,
          enable_sound_for_messages: true,
          show_stickers: false,
          allow_message_reactions: false,
          enable_collaborative_document: false,
          enable_collaborative_whiteboard: false,
          enable_message_translation: false,
          send_reply_in_private_to_group_member: false,
        },
      },
    }
    const success = yield call(chatWidgetAdd, filtered, activeApp, chatWidgetAppUrl)

    if (success && success.data) {
      yield put({
        type: actions.CHAT_WIDGETS_LIST,
        payload: {
          data: activeApp,
          id: success.data.widgetId,
          redirectToCustomize: true,
        },
      })
      window.triggerNotification('Widget created', 'success')
    } else if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
      }
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* UPDATE_CHAT_WIDGET({ payload }) {
  try {
    const { chatWidgetActive } = reduxStore.getState().misc
    const { widgetId } = reduxStore.getState().misc.chatWidgetActive

    const activeApp = reduxStore.getState().apps.active

    const filtered = { settings: chatWidgetActive.configuration }
    filtered.settings.widgetId = widgetId
    if (payload.selectedComponent === 'GLOBAL') {
      filtered.settings.style = {
        primary_color: '#03A9F4',
        foreground_color: '#000000',
        background_color: '#FFFFFF',
        override_system_background_colors: true,
        custom_css: payload.values.custom_css,
        custom_js: payload.values.custom_js,
        docked_layout_icon_open: payload.values.docked_layout_icon_open,
        docked_layout_icon_close: payload.values.docked_layout_icon_close,
        docked_layout_icon_background: payload.values.docked_layout_icon_background,
      }

      filtered.settings.name = payload.values.name
    } else if (payload.selectedComponent === 'SIDEBAR') {
      const sidebarSettings = {
        recent_chat_listing: payload.values.recent_chat_listing,
        user_listing: payload.values.user_listing,
        group_listing: payload.values.group_listing,
        chats: payload.values.sidebar_chats,
        users: payload.values.sidebar_users,
        groups: payload.values.sidebar_groups,
        calls: payload.values.sidebar_calls,
        user_settings: payload.values.sidebar_user_settings,
        sidebar_navigation_sequence: payload.values.sidebar_navigation_sequence,
        start_a_new_conversation: payload.values.start_a_new_conversation,
      }
      filtered.settings.sidebar = sidebarSettings
    } else if (payload.selectedComponent === 'MAIN') {
      const mainSettings = {
        enable_sending_messages: payload.values.enable_sending_messages,
        allow_mention_members: payload.values.allow_mention_members,
        enable_editing_messages: payload.values.enable_editing_messages,
        enable_deleting_messages: payload.values.enable_deleting_messages,
        hide_deleted_messages: payload.values.hide_deleted_messages,
        enable_replying_to_messages: payload.values.enable_replying_to_messages,
        enable_threaded_replies: payload.values.enable_threaded_replies,
        enable_share_copy_forward_messages: payload.values.enable_share_copy_forward_messages,
        highlight_messages_from_moderators: payload.values.highlight_messages_from_moderators,
        enable_voice_calling: payload.values.enable_voice_calling,
        enable_video_calling: payload.values.enable_video_calling,
        show_call_recording_option: payload.values.show_call_recording_option,
        send_photos_videos: payload.values.send_photos_videos,
        send_files: payload.values.send_files,
        send_voice_notes: payload.values.send_voice_notes,
        send_emojis: payload.values.send_emojis,
        show_emojis_in_larger_size: payload.values.show_emojis_in_larger_size,
        send_gifs: payload.values.send_gifs,
        share_live_reactions: payload.values.share_live_reactions,
        share_location: payload.values.share_location,
        set_groups_in_qna_mode_by_moderators: payload.values.set_groups_in_qna_mode_by_moderators,
        create_groups: payload.values.create_groups,
        allow_add_members: payload.values.allow_add_members,
        join_or_leave_groups: payload.values.join_or_leave_groups,
        hide_join_leave_notifications: payload.values.hide_join_leave_notifications,
        allow_delete_groups: payload.values.allow_delete_groups,
        view_group_members: payload.values.view_group_members,
        view_shared_media: payload.values.view_shared_media,
        send_message_in_private_to_group_member:
          payload.values.send_message_in_private_to_group_member,
        show_typing_indicators: payload.values.show_typing_indicators,
        show_delivery_read_indicators: payload.values.show_delivery_read_indicators,
        show_user_presence: payload.values.show_user_presence,
        show_call_notifications: payload.values.show_call_notifications,
        block_user: payload.values.block_user,
        allow_moderator_to_delete_member_messages:
          payload.values.allow_moderator_to_delete_member_messages,
        allow_kick_ban_members: payload.values.allow_kick_ban_members,
        allow_promote_demote_members: payload.values.allow_promote_demote_members,
        allow_creating_polls: payload.values.allow_creating_polls,
        enable_sound_for_messages: payload.values.enable_sound_for_messages,
        enable_sound_for_calls: payload.values.enable_sound_for_calls,
        show_stickers: payload.values.show_stickers,
        allow_message_reactions: payload.values.allow_message_reactions,
        enable_collaborative_document: payload.values.enable_collaborative_document,
        enable_collaborative_whiteboard: payload.values.enable_collaborative_whiteboard,
        enable_message_translation: payload.values.enable_message_translation,
        send_reply_in_private_to_group_member:
          payload.values.send_reply_in_private_to_group_member,
      }
      filtered.settings.main = mainSettings
    }

    const success = yield call(chatWidgetUpdate, filtered, activeApp, payload.chatWidgetAppUrl)

    if (success) {
      yield put({
        type: actions.CHAT_WIDGETS_LIST,
        payload: { data: activeApp, id: widgetId },
      })
      window.triggerNotification('Widget saved', 'success')
    } else {
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
      }
      yield put(push(`/app/${activeApp.id}/widgets`))
    }
    return success
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* BUILDER_GET({ payload }) {
  try {
    const { id } = payload
    if (!payload.data) payload.data = reduxStore.getState().apps.active

    const success = yield call(builderGet, id, payload.data)
    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          buildersActive: success.data,
        },
      })
    }
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* BUILDER_ADD({ payload, isCometChatAgent, agentId, navigateCallback }) {
  try {
    const activeApp = reduxStore.getState().apps.active

    const success = yield call(builderAdd, payload, activeApp)

    if (success && success.data) {
      localStorage.removeItem('ccPreviewBuilderId')
      localStorage.setItem('ccIsNewBuilder', 'true')
      const baseUrl = window.location.origin
      if (isCometChatAgent && agentId && navigateCallback) {
        navigateCallback(`/app/${activeApp.id}/ai-agents/${agentId}/deploy/${success.data?.builderId}`)
      } else {
        window.open(`${baseUrl}/app/${activeApp.id}/builder/${success.data?.builderId}`, '_blank')
      }
      if (isCometChatAgent) {
        const payloadTemp = {
          metadata: {
            ...activeApp.metadata,
            ccAgentBuilderId: success.data?.builderId,
          },
        }
        yield put({
          type: 'apps/EDIT',
          payload: payloadTemp,
          id: activeApp.id,
        })
      }
      yield put({
        type: actions.BUILDER_LIST,
        payload: { currentPage: 1, data: activeApp },
      })
    } else if (success.error && success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        window.triggerNotification('Plan restriction - Feature not available', 'error')
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    return success
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* BUILDER_LIST({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areBuildersLoading: true,
      },
    })

    if (!payload.data) {
      payload.data = reduxStore.getState().apps.active
    }

    const success = yield call(builderList, payload.data)
    const firstLowCodeBuilder = _.find(success.data, { type: 'low-code' })

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          builderList: success.data,
          buildersActive: firstLowCodeBuilder || success.data[0],
          areBuildersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          builderList: [],
          areBuildersLoading: false,
        },
      })
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        builderList: [],
        areBuildersLoading: false,
      },
    })
  }
}

export function* AGENT_BUILDER_LIST({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areBuildersLoading: true,
      },
    })

    if (!payload.data) {
      payload.data = reduxStore.getState().apps.active
    }
    const success = yield call(builderList, payload.data, 'agent')

    if (success && success.data) {
      const builders = success?.data || []
      const ccAgentBuilderId = payload?.data?.metadata?.ccAgentBuilderId

      const currentAgentBuilder = payload?.isCometChatAgent
        ? builders.find(b => b.builderId === ccAgentBuilderId) || null
        : builders.find(b => b.builderId !== ccAgentBuilderId) || null

      yield put({
        type: actions.SET_STATE,
        payload: {
          agentBuilderList: success.data,
          agentBuildersActive: currentAgentBuilder,
          areBuildersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          agentBuilderList: [],
          agentBuildersActive: null,
          areBuildersLoading: false,
        },
      })
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        agentBuilderList: [],
        agentBuildersActive: null,
        areBuildersLoading: false,
      },
    })
  }
}

export function* BUILDER_UPDATE({ id, payload, noCodeMessage, setIsDataSaved, callback }) {
  try {
    const activeApp = reduxStore.getState().apps.active

    const success = yield call(builderUpdate, id, payload, activeApp)

    if (!success || !success.data) {
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        window.triggerNotification('Plan restriction - Feature not available', 'error')
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    } else if (success && success.data) {
      if (typeof callback === 'function') {
        callback(success)
      }

      if (noCodeMessage) {
        window.triggerNotification(noCodeMessage, 'success')
        setIsDataSaved?.(true)
      }
    }
    return success
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* BUILDER_RESET({ id }) {
  try {
    const activeApp = reduxStore.getState().apps.active

    const success = yield call(builderReset, id, activeApp)

    if (success && success.data) {
      yield put({
        type: actions.BUILDER_GET,
        payload: { id, data: activeApp },
      })
      window.triggerNotification(
        'UI Kit Builder has been successfully reset to its default settings!',
        'success',
      )
    } else if (success.error && success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        window.triggerNotification('Plan restriction - Feature not available', 'error')
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    return success
  } catch (e) {
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* NOTIFICATION_LOGS({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        isNotificationLogsLoading: true,
      },
    })

    if (!payload.data) payload.data = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps
    const { notificationLogsPagination: logsPagination, notificationLogsFilters: storedFilters = {} } = reduxStore.getState().misc

    const shouldUseStoredFilters = (payload.paginationType && !payload.filters) || payload.filters === undefined
    const filters = shouldUseStoredFilters ? storedFilters : payload.filters

    const { paginationType, pageNo } = payload
    let paginationPayload = ''
    if (paginationType === 'next' && logsPagination && logsPagination.next !== undefined) {
      paginationPayload = objectToParams(logsPagination.next)
    } else if (
      paginationType === 'previous' &&
      logsPagination &&
      logsPagination.previous !== undefined
    ) {
      paginationPayload = objectToParams(logsPagination.previous)
    }

    const success = yield call(
      getNotificationLogs,
      payload.data,
      getAppHost(appSettings),
      paginationPayload,
      filters,
    )

    if (success && success.data) {
      let totalLogCount = success.data.length
      let notificationLogsPagination = {}

      if (success.meta) {
        notificationLogsPagination = success.meta

        const limit = success.meta.current?.limit || 10

        const hasNextPage = success.meta.next &&
          ((Array.isArray(success.meta.next) && success.meta.next.length > 0) ||
           (typeof success.meta.next === 'object' && !Array.isArray(success.meta.next) && Object.keys(success.meta.next).length > 0))

        if (pageNo !== undefined && pageNo > 1) {
          if (hasNextPage) {
            totalLogCount = pageNo * limit + 1
          } else {
            totalLogCount = (pageNo - 1) * limit + success.data.length
          }
        } else if (hasNextPage) {
          totalLogCount = limit + 1
        } else {
          totalLogCount = success.data.length
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          notificationLogs: success.data,
          notificationLogsTotal: totalLogCount,
          notificationLogsPagination,
          notificationLogsFilters: filters,
          isNotificationLogsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          notificationLogs: [],
          notificationLogsTotal: 0,
          notificationLogsPagination: {},
          isNotificationLogsLoading: false,
        },
      })
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        notificationLogs: [],
        notificationLogsTotal: 0,
        notificationLogsPagination: {},
        isNotificationLogsLoading: false,
      },
    })
  }
}

export function UPDATE_GET_STARTED_FEATURE_SCREEN_BEHAVIOUR(action) {
  const STORAGE_KEY = 'cc_getStartedScreenBehaviour'
  const defaultBehaviour = {
    moderation: false,
    chat: false,
    calling: false,
  }

  const { feature, value } = action.payload

  const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || defaultBehaviour

  const updated = {
    ...stored,
    [feature]: value,
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
}

export function* INITIATE_NOTIFICATION({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: {
      notification_type: payload.type,
      notification_message: payload.message,
    },
  })
}

export function* RESET_NOTIFICATION() {
  yield put({
    type: actions.SET_STATE,
    payload: {
      notification_type: 'info',
      notification_message: '',
    },
  })
}

export function* SHOW_INTENT_REQUEST({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: {
      currentIntentRequest: '',
    },
  })
  const { intent: currentIntentRequest } = payload
  yield put({
    type: actions.SET_STATE,
    payload: {
      currentIntentRequest,
    },
  })
  yield put({
    type: 'settings/SET_STATE',
    payload: {
      visibleModalID: 'intentRequest',
    },
  })
}

export function* HIDE_INTENT_REQUEST() {
  yield put({
    type: actions.SET_STATE,
    payload: {
      currentIntentRequest: '',
    },
  })
  yield put({
    type: 'settings/SET_STATE',
    payload: {
      visibleModalID: '',
    },
  })
}

export default function* miscSaga() {
  yield all([
    takeEvery(actions.BOTS_LIST, BOTS_LIST),
    takeEvery(actions.GET_BOT, GET_BOT),
    takeEvery(actions.ADD_BOT, ADD_BOT),
    takeEvery(actions.UPDATE_BOT, withCallback(UPDATE_BOT)),
    takeEvery(actions.DELETE_BOT, DELETE_BOT),
    takeEvery(actions.CHANGE_BOT_STATUS, CHANGE_BOT_STATUS),
    takeEvery(actions.CHAT_WIDGETS_LIST, withCallback(CHAT_WIDGETS_LIST)),
    takeEvery(actions.GET_CHAT_WIDGET, GET_CHAT_WIDGET),
    takeEvery(actions.ADD_CHAT_WIDGET, withCallback(ADD_CHAT_WIDGET)),
    takeEvery(actions.UPDATE_CHAT_WIDGET, withCallback(UPDATE_CHAT_WIDGET)),
    takeLatest(actions.BUILDER_GET, withCallback(BUILDER_GET)),
    takeLatest(actions.BUILDER_ADD, withCallback(BUILDER_ADD)),
    takeLatest(actions.BUILDER_LIST, withCallback(BUILDER_LIST)),
    takeLatest(actions.AGENT_BUILDER_LIST, withCallback(AGENT_BUILDER_LIST)),
    takeEvery(actions.BUILDER_UPDATE, withCallback(BUILDER_UPDATE)),
    takeLatest(actions.BUILDER_RESET, withCallback(BUILDER_RESET)),
    takeEvery(actions.NOTIFICATION_LOGS, withCallback(NOTIFICATION_LOGS)),
    takeEvery(actions.UPDATE_GET_STARTED_FEATURE_SCREEN_BEHAVIOUR, UPDATE_GET_STARTED_FEATURE_SCREEN_BEHAVIOUR),
    takeEvery(actions.INITIATE_NOTIFICATION, INITIATE_NOTIFICATION),
    takeEvery(actions.RESET_NOTIFICATION, RESET_NOTIFICATION),
    takeEvery(actions.SHOW_INTENT_REQUEST, SHOW_INTENT_REQUEST),
    takeEvery(actions.HIDE_INTENT_REQUEST, HIDE_INTENT_REQUEST),
  ])
}
